import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Grid,
  Checkbox,
  ListItemText,
  Autocomplete,
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import * as endPoints from '../assets/js/endpoints';
import Loader from '../components/loader';
import { getEmployeeColor } from '../constants/employeeColors';
import { MONTHS, MONTH_LABELS } from '../constants/months';
import StatsSmallCard from '../components/StatsSmallCard';
import { useNotification } from '../assets/contexts/notificationContext';
import ActivityAttendanceChart from './activityAttendanceChart';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const StatsCards = () => {
  const [employeeStats, setEmployeeStats] = useState([]);
  const [clientStats, setClientStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const { showNotification } = useNotification();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [employeeData, clientData] = await Promise.all([
          endPoints.getEmployeeStats(),
          endPoints.getTopClientsMonthly(),
        ]);
        setEmployeeStats(employeeData);
        setClientStats(clientData);
      } catch (_err) {
        showNotification('Error obteniendo estadísticas', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) return <Loader fullscreen={false} />;

  const filteredEmployeeStats = employeeStats.filter(d =>
    selectedMonths.includes(d.month.slice(5, 7))
  );
  const monthsFiltered = selectedMonths;
  const employees = [...new Set(employeeStats.map(d => d.employee_name))];

  const employeeChartData = {
    labels: monthsFiltered.map(m => MONTH_LABELS[m]),
    datasets: employees.map(name => {
      const item = employeeStats.find(d => d.employee_name === name);
      return {
        label: name,
        backgroundColor: item?.color || getEmployeeColor(item?.employee_id),
        data: monthsFiltered.map(month => {
          const entry = employeeStats.find(
            d => d.month.slice(5, 7) === month && d.employee_name === name
          );
          return entry ? entry.total : 0;
        })
      };
    })
  };

  const hasEmployeeData = employeeChartData.datasets.some(ds => ds.data.some(v => v > 0));

  const clientTotals = {};
  selectedMonths.forEach(m => {
    Object.entries(clientStats).forEach(([key, list]) => {
      if (key.slice(5, 7) === m) {
        list.forEach(c => {
          const safeClientName = (c.client_name && c.client_name !== 'null') ? c.client_name : 'Sin cliente';
          clientTotals[safeClientName] = (clientTotals[safeClientName] || 0) + c.total;
        });
      }
    });
  });
  const topData = Object.entries(clientTotals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const topClientsChartData = {
    labels: topData.map(d => (Array.isArray(d) ? d[0] : d.client_name)),
    datasets: [{
      label: 'Visitas',
      backgroundColor: '#b4c7be',
      data: topData.map(d => (Array.isArray(d) ? d[1] : d.total))
    }]
  };

  const hasClientData = topData.length > 0;

  const totalVisits = filteredEmployeeStats.reduce((sum, d) => sum + d.total, 0);
  const uniqueClients = new Set(
    selectedMonths.flatMap(m =>
      Object.entries(clientStats)
        .filter(([key]) => key.slice(5, 7) === m)
        .flatMap(([, list]) => list.map(c => c.client_id))
    )
  ).size;
  const employeeTotals = {};
  filteredEmployeeStats.forEach(d => {
    employeeTotals[d.employee_name] = (employeeTotals[d.employee_name] || 0) + d.total;
  });
  const topEmployee = Object.entries(employeeTotals).sort((a, b) => b[1] - a[1])[0];

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    normalized: true,
    elements: {
      bar: {
        borderWidth: 0,
        borderRadius: 2,
      },
    },
    plugins: {
      legend: {
        labels: {
          color: '#4a4a4a',
          font: {
            size: 14,
            weight: '600',
          },
        },
      },
      tooltip: {
        bodyFont: { size: 13 },
        titleFont: { size: 13, weight: '600' },
      },
    },
    scales: {
      x: {
        alignToPixels: true,
        ticks: {
          color: '#555',
          font: { size: 14, weight: '500' },
        },
        grid: { color: '#d9dde1' },
      },
      y: {
        alignToPixels: true,
        beginAtZero: true,
        ticks: {
          color: '#555',
          font: { size: 14, weight: '500' },
        },
        grid: { color: '#d9dde1' },
      },
    },
  };

  return (
    <>
        <Grid container spacing={2} sx={{ mt: 7, justifyContent:'space-around' }}>
          <Grid item xs={12} md={3}  sx={{alignSelf:'center'}}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={MONTHS}
              value={selectedMonths}
              onChange={(_event, newValue) => setSelectedMonths(newValue)}
              getOptionLabel={option => MONTH_LABELS[option]}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} />
                  <ListItemText primary={MONTH_LABELS[option]} />
                </li>
              )}
              renderInput={params => (
                <TextField {...params} label="Meses" fullWidth />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <StatsSmallCard title="Total visitas" value={totalVisits} sx={{ height: '100%' }} />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <StatsSmallCard title="Clientes" value={uniqueClients} color="secondary" sx={{ height: '100%' }} />
          </Grid>
          {topEmployee && (
            <Grid item xs={12} sm={4} md={3}>
              <StatsSmallCard
                title="Empleado líder"
                value={`${topEmployee[0]} (${topEmployee[1]})`}
                sx={{ height: '100%' }}
              />
            </Grid>
          )}
        </Grid>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ width: '100%' }}>
              <CardHeader title="Visitas por empleado" subheader="por mes" />
              <CardContent>
                {hasEmployeeData ? (
                  <Bar
                    data={employeeChartData}
                    options={chartOptions}
                    style={{ width: '100%', height: 300 }}
                  />
                ) : (
                  <Typography>No hay citas en el mes seleccionado</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ width: '100%' }}>
              <CardHeader title="Top clientes" subheader="más visitas" />
              <CardContent>
                {hasClientData ? (
                  <Bar
                    data={topClientsChartData}
                    options={chartOptions}
                    style={{ width: '100%', height: 300 }}
                  />
                ) : (
                  <Typography>No hay citas en el mes seleccionado</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
             <Grid item xs={12} md={12}>
           <ActivityAttendanceChart />
           </Grid>
        </Grid>
    </>
  );
};

export default StatsCards;
